<template>
    <div class="topbar-item">
        <div
            class="btn btn-icon w-auto btn-clean d-flex align-items-center btn-lg px-2"
            id="kt_quick_user_toggle"
        >
            <span class="symbol symbol-35 symbol-light-success">
                <span v-if="currentUser.photo">
                    <b-avatar :src="currentUser.photo" size="2rem"></b-avatar>
                </span>
                <span v-else class="font-size-h5 font-weight-bold">
                    <b-avatar :src="defaultAvatar" size="2rem"></b-avatar>
                </span>
            </span>
            <span class="font-size-base d-inline ml-3">
                <div class="text-dark-50 font-weight-bolder nav-user-name" :title="getInitials">
                    {{ getInitials }}
                    <i v-if="currentUser.roe_id_details?.account_id && currentUser.qualified" class="fas fa-award mr-2 text-warning"></i>
                </div>
                <!-- Simplified function display in header -->
                <div class="text-dark-50" v-if="userFunctionsSimple.length">
                    <div v-for="(func, index) in userFunctionsSimple" :key="func" class="function-line">
                        {{ func }}<span v-if="index < userFunctionsSimple.length - 1">, </span>
                    </div>
                </div>
                <!-- <p v-if="isSuperAdmin && organisation.name" class="text-primary font-weight-bold">{{organisation.name}}</p> -->
            </span>
        </div>

        <div id="kt_quick_user" ref="kt_quick_user" class="offcanvas offcanvas-right p-10">
            <!--begin::Header-->
            <div class="offcanvas-header d-flex align-items-center justify-content-between pb-5">
                <h3 class="font-weight-bold m-0">
                    {{ $t('PROFILE.HEADER') }}
                </h3>
                <a
                    href="#"
                    class="btn btn-xs btn-icon btn-light btn-hover-primary"
                    id="kt_quick_user_close"
                >
                    <b-icon-x aria-hidden="true" font-scale='1.5'></b-icon-x>
                </a>
            </div>
            <!--end::Header-->

            <!--begin::Content-->
            <perfect-scrollbar
                class="offcanvas-content pr-1 mr-n5 scroll"
                style="max-height: 90vh; position: relative; max-width: 100%;"
            >
                <!--begin::Header-->
                <div class="d-flex align-items-center mt-5">
                    <div class="symbol symbol-100 mr-5">
                        <span v-if="currentUser.photo">
                            <img class="symbol-label" :src="currentUserPersonalInfo.photo" alt="" />
                        </span>
                        <span v-else>
                            <img class="symbol-label" :src="defaultAvatar" alt="" />
                        </span>
                        <i class="symbol-badge bg-success"></i>
                       <div v-if="currentUser.roe_id_details?.account_id && currentUser.qualified"
                            class="label label-lg label-inline label-light-success ml-4 font-weight-bold">
                            <i class="fas fa-award mr-2 text-warning"></i>
                            {{ $t("PROFILE.ROEID_BADGE_LABEL") }}
                        </div>
                    </div>
                    <div class="d-flex flex-column overflow-hidden">
                        <router-link
                            to="/profile"
                            class="font-weight-bold font-size-h5 text-dark-75 text-hover-primary"
                            @click.native="closeOffcanvas"
                        >
                            {{ getFullName }}
                        </router-link>
                        <!-- Functions Accordion -->
                        <div class="mt-2" v-if="userFunctions.length">
                            <div class="font-weight-bold text-muted font-size-sm mb-2">{{ $t('FORM_FIELDS.ORGANIZATIONAL_ASSIGNMENTS') }}</div>
                            <div v-for="(department, deptIndex) in userFunctions" :key="deptIndex" class="mb-3">
                                <!-- Department Header -->
                                <button
                                    @click="toggleAccordion(`dept-${deptIndex}`)"
                                    class="btn btn-link p-0 text-left d-flex align-items-center font-size-sm font-weight-bold"
                                    style="text-decoration: none; color: #3F4254; border: none; background: none;"
                                >
                                    <i :class="['fas', openAccordions[`dept-${deptIndex}`] ? 'fa-chevron-down' : 'fa-chevron-right', 'mr-2', 'accordion-icon']"></i>
                                    {{ department.department }}
                                </button>
                                
                                <!-- Department Content (when expanded) -->
                                <div v-if="openAccordions[`dept-${deptIndex}`]" class="mt-1 ml-3">
                                    <div v-for="(context, contextIndex) in department.contexts" :key="contextIndex" class="mb-2">
                                        <!-- Context Header -->
                                        <button
                                            @click="toggleAccordion(`context-${deptIndex}-${contextIndex}`)"
                                            class="btn btn-link p-0 text-left d-flex align-items-center font-size-m"
                                            style="text-decoration: none; color: #7E8299; border: none; background: none;"
                                        >
                                            <i :class="['fas', openAccordions[`context-${deptIndex}-${contextIndex}`] ? 'fa-chevron-down' : 'fa-chevron-right', 'mr-2', 'accordion-icon']"></i>
                                            {{ context.context }}
                                        </button>
                                        
                                        <!-- Functions (when context expanded) -->
                                        <div v-if="openAccordions[`context-${deptIndex}-${contextIndex}`]" class="mt-1 ml-3">
                                            <div class="function-list">
                                                <span 
                                                    v-for="func in context.functions" 
                                                    :key="func"
                                                    class="badge badge-light-primary mr-1 mb-1 font-size-m"
                                                >
                                                    {{ func }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="navi mt-2">
                            <a href="#" class="navi-item">
                                <span class="navi-link p-0 pb-2">
                                    <span class="navi-icon mr-1">
                                        <i class="fas fa-envelope-open-text"></i>
                                    </span>
                                    <span class="navi-text text-hover-primary">
                                        {{ currentUser.email }}
                                    </span>
                                </span>
                            </a>
                        </div>
                        <st-button
                          id="btn_logout"
                          :callback="onLogout">
                            {{ $t('PROFILE.LOGOUT_BUTTON_TEXT') }}
                        </st-button>
                    </div>
                </div>
                <!--end::Header-->
                <div class="separator separator-dashed mt-8 mb-5"></div>
                <!--begin::Nav-->
                <div class="navi navi-spacer-x-0 p-0">
                    <router-link
                        to="/profile"
                        class="navi-item"
                        @click.native="closeOffcanvas"
                    >
                        <div class="navi-link">
                            <div class="symbol symbol-40 bg-light mr-3">
                                <div class="symbol-label">
                                    <i class="fas fa-user"></i>
                                </div>
                            </div>
                            <div class="navi-text">
                                <div class="st-dark-body font-weight-bolder">
                                    {{ $t(`PROFILE.OPTIONS.MY_PROFILE.NAME`) }}
                                </div>
                                <div>
                                    <span class="st-dark-caption">
                                        {{ $t(`PROFILE.OPTIONS.MY_PROFILE.DESCRIPTION`) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </router-link>
                    <div
                        v-if="!currentUser?.roe_id_details"
                        class="navi-item"
                        @click="onResetPassword"
                    >
                        <div class="navi-link">
                            <div class="symbol symbol-40 bg-light mr-3">
                                <div class="symbol-label">
                                    <i class="fas fa-shield-alt"></i>
                                </div>
                            </div>
                            <div class="navi-text">
                                <div>
                                    <div class="st-dark-body font-weight-bolder">
                                        {{ $t(`PROFILE.OPTIONS.RESET_PASSWORD.NAME`) }}
                                    </div>
                                </div>
                                <div>
                                    <span class="st-dark-caption">
                                        {{ $t(`PROFILE.OPTIONS.RESET_PASSWORD.DESCRIPTION`) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="isCitizen && !currentUser?.roe_id_details"
                        class="navi-item"
                        @click="linkRoeid"
                    >
                        <div class="navi-link">
                            <div class="symbol symbol-40 bg-light mr-3">
                                <div class="symbol-label">
                                    <i class="fas fa-shield-alt"></i>
                                </div>
                            </div>
                            <div class="navi-text">
                                <div>
                                    <div class="st-dark-body font-weight-bolder">
                                        {{ $t(`PROFILE.OPTIONS.LINK_ROEID.NAME`) }}
                                    </div>
                                </div>
                                <div>
                                    <span class="st-dark-caption">
                                        {{ $t(`PROFILE.OPTIONS.LINK_ROEID.DESCRIPTION`) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>                    
                    <div
                        v-if="currentUser?.roe_id_details?.account_id"
                        class="navi-item"
                        @click="unlinkRoeid"
                    >
                        <div class="navi-link">
                            <div class="symbol symbol-40 bg-light mr-3">
                                <div class="symbol-label">
                                    <i class="fas fa-shield-alt"></i>
                                </div>
                            </div>
                            <div class="navi-text">
                                <div class="st-dark-body font-weight-bolder">
                                    {{ $t(`PROFILE.OPTIONS.UNLINK_ROEID.NAME`) }}
                                </div>
                                <div>
                                    <span class="st-dark-caption">
                                        {{ $t(`PROFILE.OPTIONS.UNLINK_ROEID.DESCRIPTION`) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="navi-item" v-if="currentUser?.pcuel_account_id">
                        <div class="navi-link">
                            <div class="symbol symbol-40 bg-light mr-3">
                                <div class="symbol-label">
                                    <i class="fas fa-shield-alt"></i>
                                </div>
                            </div>
                            <div class="navi-text">
                                <div class="st-dark-body font-weight-bolder">
                                    {{ $t(`PROFILE.OPTIONS.PCUEL.NAME`) }}
                                </div>
                                <div class="d-flex flex-column">
                                    <!--<span class="st-dark-caption">
                                        {{ $t(`PROFILE.OPTIONS.PCUEL.DESCRIPTION`, [lastPcuelSyncDate]) }}
                                    </span>-->
                                    <div class="d-flex flex-row gap-2">
                                        <!--<st-button size="small" :callback="refreshLastSync" class="mr-2 mt-2">{{ $t(`PROFILE.OPTIONS.PCUEL.BUTTON`) }}</st-button>
                                        <st-button size="small" variant="danger" :callback="deactivatePcuel" class="mt-2 flex-grow-1">{{ $t(`PROFILE.DEACTIVATE_PCUEL_BUTTON`) }}</st-button>-->
                                        <st-button size="small" variant="danger" :callback="deactivatePcuel" class="mt-2">{{ $t(`PROFILE.DEACTIVATE_PCUEL_BUTTON`) }}</st-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- TODO: (re) add v-can:read="'notificationsPreferences'" once notifications permissions is done - BE side-->
                    <router-link
                        to="/notifications"
                        class="navi-item"
                        @click.native="closeOffcanvas"
                    >
                        <div class="navi-link">
                            <div class="symbol symbol-40 bg-light mr-3">
                                <div class="symbol-label">
                                    <i class="fas fa-bell"></i>
                                </div>
                            </div>
                            <div class="navi-text">
                                <div class="st-dark-body font-weight-bolder">
                                    {{ $t(`PROFILE.OPTIONS.NOTIFICATIONS_MANAGEMENT.NAME`) }}
                                </div>
                                <div>
                                    <span class="st-dark-caption">
                                        {{ $t(`PROFILE.OPTIONS.NOTIFICATIONS_MANAGEMENT.DESCRIPTION`) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </router-link>
                </div>
                <!--end::Nav-->
            </perfect-scrollbar>
            <!--end::Content-->
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import KTLayoutQuickUser from '@/assets/js/layout/extended/quick-user.js';
import StOffcanvas from '@/assets/js/components/offcanvas.js';
import { LOGOUT, SEND_PASSWORD_RESET_EMAIL } from '../../../../auth/auth-store';
import Roles from '@/security/roles';
import { BIconX } from 'bootstrap-vue';
import moment from 'moment';
import Message from '@/shared/message/message';

export default {
    name: 'QuickUser',
    components: {
        BIconX,
    },
    data() {
        return {
            openAccordions: {},
        };
    },
    methods: {
        ...mapActions({
            logout: `auth/${LOGOUT}`,
            sendPasswordResetEmail: `auth/${SEND_PASSWORD_RESET_EMAIL}`,
            deactivateRoeId: `auth/deactivateRoeId`,
            refreshPcuelLastSync: 'auth/getLastPcuelSync',
            updateProfile: "auth/updateProfile",
        }),
        toggleAccordion(index) {
            this.$set(this.openAccordions, index, !this.openAccordions[index]);
        },        
        onLogout() {
            this.$alert({
                type: 'warning',
                text: this.$t('LOGOUT.CONFIRMATION_MESSAGE'),
                confirmButtonText: this.$t('GENERAL.YES'),
                cancelButtonText: this.$t('GENERAL.NO'),
                hasConfirmation: true,
                confirmCallback: () => {
                    this.logout().then(() => this.$router.push({ name: 'signin' }));
                }
            });
        },
        onResetPassword() {
            this.$alert({
                type: 'warning',
                text: this.$t('RESET_PASSWORD.CONFIRMATION_MESSAGE'),
                confirmButtonText: this.$t('GENERAL.YES'),
                cancelButtonText: this.$t('GENERAL.NO'),
                hasConfirmation: true,
                confirmCallback: () => {
                    const { email } = this.currentUser;
                    this.sendPasswordResetEmail({ email }).then(() => {
                        this.$alert({
                            type: 'success',
                            text: this.$t('RESET_PASSWORD.SUCCESSFULLY_SENT_MESSAGE'),
                            cancelButtonText: this.$t('GENERAL.CLOSE'),
                            showCancelButton: true,
                            showConfirmButton: false,
                        });
                    });
                }
            });
        },
        unlinkRoeid() {
            this.$alert({
                type: 'warning',
                text: this.$t('UNLINK_ROEID.CONFIRMATION_MESSAGE'),
                confirmButtonText: this.$t('GENERAL.YES'),
                cancelButtonText: this.$t('GENERAL.NO'),
                hasConfirmation: true,
                confirmCallback: () => {
                    this.deactivateRoeId().then(() => {
                        this.$alert({
                            type: 'success',
                            text: this.$t('UNLINK_ROEID.SUCCESSFULLY_MESSAGE'),
                            cancelButtonText: this.$t('GENERAL.CLOSE'),
                            showCancelButton: true,
                            showConfirmButton: false,
                        });
                        this.logout().then(() => this.$router.push({ name: 'signin' }));
                    });
                }
            });
        },
        linkRoeid() {
            this.$alert({
                type: 'warning',
                text: this.$t('LINK_ROEID.INFORMATION_MESSAGE'),
                confirmButtonText: this.$t('GENERAL.CLOSE'),
                cancelButtonText: this.$t('GENERAL.NO'),
                hasCancelButton: false,
                width: this.dialogWidth,
                confirmCallback: () => {
                
                }
            });
        },        
        closeOffcanvas() {
            new StOffcanvas(KTLayoutQuickUser.getElement()).hide();
        },
        async refreshLastSync() {
            await this.refreshPcuelLastSync(this.currentUser?.pcuel_account_id);
        },
        deactivatePcuel() {
            this.$alert({
                type: 'warning',
                text: this.$t('PCUEL.UNLINK_CONFIRMATION_MESSAGE'),
                confirmButtonText: this.$t('GENERAL.YES'),
                cancelButtonText: this.$t('GENERAL.NO'),
                hasConfirmation: true,
                confirmCallback: () => {
                    const payload = this.currentUser;
                    delete payload?.pcuel_account_id;

                    this.updateProfile(payload).then(() => {
                        Message.success("PCUEL.UNLINK_SUCCESS");
                    }).catch(() => {
                        Message.error('PCUEL.UNLINK_ERROR')
                    });
                }
            });
        }
    },
    computed: {
        ...mapGetters({
            currentUser: 'auth/currentUser',
            accountDetails: 'auth/accountDetails',
            defaultAvatar: 'shared/defaultAvatar',
            lastPcuelSync: 'auth/lastPcuelSync'
        }),
        isCitizen() {
        return [Roles.values["citizen"], Roles.values["legal_entity"]].includes(this.currentUser.user_type_name);
        },       
        getFullName() {
            if (!this.currentUser?.first_name || !this.currentUser?.last_name) return this.currentUser.email;

            return `${this.currentUser.first_name} ${this.currentUser.last_name}`
        },
        getInitials() {
            if (this.isMobileDevice) {
                return `${this.currentUser?.first_name?.slice(0, 1)}.${this.currentUser?.last_name?.slice(0, 1)}.`;
            }

            return this.currentUser?.first_name && this.currentUser?.last_name ? 
                `${this.currentUser?.first_name} ${this.currentUser?.last_name}` : this.currentUser.email;
        },
        // userFunctions() {
        //     // TODO: Temporary solution
        //     if (this.currentUser.user_type_name !== 'staff') return '';
        //     return this.accountDetails?.functions?.length ? this.accountDetails.functions : '';
        // },
        userFunctions() {
            if (this.currentUser.user_type_name !== 'staff') return [];
            // const assignments = organizational_assignments || [];
            const assignments = this.accountDetails?.organizational_assignments || [];
            const departmentsList = [];

            assignments.forEach(dept => {
                const departmentGroup = {
                    department: dept.department_name,
                    contexts: []
                };

                // Department functions
                if (dept.department_functions?.length) {
                    departmentGroup.contexts.push({
                        context: this.$t('FORM_FIELDS.DEPARTMENT_FUNCTIONS'),
                        contextType: 'department',
                        functions: dept.department_functions.map(f => f.function_name)
                    });
                }

                // Service functions
                dept.services?.forEach(service => {
                    if (service.service_functions?.length) {
                        departmentGroup.contexts.push({
                            context: `${this.$t('FORM_FIELDS.SERVICE')}: ${service.service_name}`,
                            contextType: 'service',
                            functions: service.service_functions.map(f => f.function_name)
                        });
                    }

                    // Service compartment functions
                    service.service_compartments?.forEach(serviceComp => {
                        if (serviceComp.service_compartment_functions?.length) {
                            departmentGroup.contexts.push({
                                context: `${service.service_name} / ${serviceComp.compartment_name}`,
                                contextType: 'service_compartment',
                                functions: serviceComp.service_compartment_functions.map(f => f.function_name)
                            });
                        }
                    });
                });

                // Department compartment functions
                dept.compartments?.forEach(comp => {
                    if (comp.compartment_functions?.length) {
                        departmentGroup.contexts.push({
                            context: `${comp.compartment_name}`,
                            contextType: 'compartment',
                            functions: comp.compartment_functions.map(f => f.function_name)
                        });
                    }
                });

                if (departmentGroup.contexts.length > 0) {
                    departmentsList.push(departmentGroup);
                }
            });

            return departmentsList;
        },
        userFunctionsSimple() {
            const allFunctions = this.userFunctions.flatMap(dept => 
                dept.contexts.flatMap(context => context.functions)
            );
            return allFunctions.slice(0, 2); // Show only first 2 functions in header
        },       
        isSuperAdmin() {
            return this.currentUser.user_type_name === Roles.values['super_admin'];
        },
        isMobileDevice() {
            return KTUtil.isMobileDevice();
        },
        dialogWidth() {
            return this.isMobileDevice ? '90%' : '30%';
        },
        lastPcuelSyncDate() {
            if (this.lastPcuelSync) {
                return moment(this.lastPcuelSync?.updated_date).format('DD.MM.YYYY');
            }

            return '-'
        }
    },
    mounted() {
        // Init Quick User Panel
        KTLayoutQuickUser.init(this.$refs['kt_quick_user']);
    }
};
</script>

<style scoped lang="scss">
@import "@/assets/sass/pages/profile/quick-user.scss";

.accordion-icon {
    transition: transform 0.3s ease;
    font-size: 0.75rem;
}

// .function-list {
//     .badge {
//         font-size: 0.7rem;
//     }
// }

// Optional: Add hover effects
.btn-link:hover .accordion-icon {
    color: #3699FF;
}

.custom-alignment {
    position: absolute; 
    bottom: -10px; 
    left: 50%; 
    transform: translateX(-50%);
    font-size: 1.5rem;
}
</style>
